// Draws the customer LINE menu picture (2500×1336) on a canvas:
// brand bar (salon's logo picture and/or name) → banner (salon's photo) → 4 buttons in the chosen colour theme.
// A slot with nothing in it stays blank. No React or DOM lookups here, so scripts/make-richmenu.mjs can reuse it.
import { BRANDED_HEIGHT, BRANDED_LAYOUT, MENU_WIDTH } from '@core/richmenu.ts';

export interface MenuTheme {
  id: string;
  label: string;
  bg: string;      // page background
  brand: string;   // shop name text
  card: string;    // button fill
  line: string;    // button border + icons + thin rules
  text: string;    // button label
  muted: string;   // button sub-label, tagline
  blank: string;   // empty banner
}

/** The first theme is the default for new salons. */
export const THEMES: MenuTheme[] = [
  { id: 'ivory', label: 'ขาวทอง', bg: '#fbf8f2', brand: '#9a7633', card: '#ffffff', line: '#c3a062', text: '#2b2418', muted: '#8a7d66', blank: '#f1eadb' },
  { id: 'noir', label: 'ดำทอง', bg: '#111111', brand: '#d4b06a', card: '#1b1b1b', line: '#c9a45c', text: '#f6efe2', muted: '#b0a284', blank: '#1e1b16' },
  { id: 'rose', label: 'โรสโกลด์', bg: '#fbf2ef', brand: '#a35a66', card: '#ffffff', line: '#c48b8f', text: '#4a2c33', muted: '#94737a', blank: '#f3e1dc' },
  { id: 'sage', label: 'เขียวเสจ', bg: '#eef2ea', brand: '#4f6b4e', card: '#ffffff', line: '#7f9a7c', text: '#2e3a2d', muted: '#717f6e', blank: '#dde6d8' },
  { id: 'navy', label: 'กรมท่าทอง', bg: '#0f1b2d', brand: '#d8b673', card: '#17263d', line: '#c9a45c', text: '#f1f4f9', muted: '#a3b0c4', blank: '#172640' },
  { id: 'lavender', label: 'ลาเวนเดอร์', bg: '#f4f0fa', brand: '#6f559a', card: '#ffffff', line: '#9c86c4', text: '#3a2d52', muted: '#7f7396', blank: '#e6def3' },
  { id: 'pearl', label: 'ขาวมินิมอล', bg: '#f7f6f2', brand: '#1d1d1d', card: '#ffffff', line: '#2b2b2b', text: '#1d1d1d', muted: '#7b776f', blank: '#ebe8e1' },
];

export const FONT = '"Noto Sans Thai", "Leelawadee UI", "Sukhumvit Set", Thonburi, Tahoma, sans-serif';

/** Line icons on a 24×24 grid (drawn as strokes). */
const ICONS = {
  calendar: 'M4 6.5h16v13.5H4z M4 10.5h16 M8.5 4v4 M15.5 4v4 M9 15.2l2 2 4-4',
  list: 'M6 4h12v16H6z M9 9h6 M9 12.5h6 M9 16h4',
  sparkle: 'M12 3.5l1.9 5.6 5.6 1.9-5.6 1.9L12 18.5l-1.9-5.6L4.5 11l5.6-1.9z',
  pin: 'M12 21s-6.5-6.1-6.5-11.2a6.5 6.5 0 0 1 13 0C18.5 14.9 12 21 12 21z M14.5 9.8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z',
  phone: 'M6.6 3.8h2.9l1.4 4.1-2 1.4a11.5 11.5 0 0 0 5.8 5.8l1.4-2 4.1 1.4v2.9a1.8 1.8 0 0 1-1.9 1.8A16.4 16.4 0 0 1 4.8 5.7a1.8 1.8 0 0 1 1.8-1.9z',
  user: 'M16 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0z M4.5 20.5a7.5 7.5 0 0 1 15 0',
  plus: 'M12 5v14 M5 12h14',
  sliders: 'M4 7h9 M17 7h3 M4 17h3 M11 17h9 M15 5v4 M9 15v4',
} as const;
type IconName = keyof typeof ICONS;

/** Same order as customerButtons() in core/richmenu.ts (the tap areas). Sub-labels stay short: they are small on a phone. */
export const BUTTONS: { icon: IconName; label: string; sub: string }[] = [
  { icon: 'calendar', label: 'จองคิว', sub: 'เลือกช่างและเวลา' },
  { icon: 'list', label: 'การจองของฉัน', sub: 'ดู · เลื่อน · ยกเลิก' },
  { icon: 'phone', label: 'โทรหาร้าน', sub: 'คุยกับร้าน' },
  { icon: 'sparkle', label: 'บริการและราคา', sub: 'ราคาและเวลาเปิด' },
];

type Img = CanvasImageSource & { width: number; height: number };

export interface MenuArt {
  theme: MenuTheme;
  /** Shop name shown in the brand bar (beside a square logo, or alone). Empty = not shown. */
  name?: string;
  tagline?: string;
  /** Salon's logo / name picture for the brand bar (fitted inside, never cropped). */
  logo?: Img | null;
  /** Salon's photo for the banner (fills the banner, cropped to fit). */
  photo?: Img | null;
  /** Which part of the photo stays in view when it is cropped: 0 = top, 50 = middle, 100 = bottom. */
  photoFocus?: number;
}

const PAD = 40;
const GAP = 28;
/** A logo at least this many times wider than tall is a full "name" logo and is shown alone. */
const WIDE_LOGO = 2.2;
const isLatin = (s: string) => /^[\x20-\x7e]*$/.test(s);
const hasSize = (i: Img | null | undefined): i is Img => !!i && i.width > 0 && i.height > 0;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function setFont(ctx: CanvasRenderingContext2D, px: number, weight: number, spacing: number) {
  ctx.font = `${weight} ${px}px ${FONT}`;
  const ls = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in ls) ls.letterSpacing = `${Math.round(spacing * px)}px`;
}

function resetSpacing(ctx: CanvasRenderingContext2D) {
  const ls = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in ls) ls.letterSpacing = '0px';
}

/** Largest font size (from `size` down) at which `s` fits in `maxW`. Leaves that font set. */
function fitSize(ctx: CanvasRenderingContext2D, s: string, maxW: number, size: number, weight: number, spacing = 0): { px: number; width: number } {
  let px = size;
  for (;;) {
    setFont(ctx, px, weight, spacing);
    const width = ctx.measureText(s).width;
    if (width <= maxW || px <= 24) return { px, width };
    px -= 4;
  }
}

/** One line of text, shrunk until it fits `maxW`. Returns the drawn width. */
function fitText(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, maxW: number, size: number, weight: number, spacing = 0): number {
  const { width } = fitSize(ctx, s, maxW, size, weight, spacing);
  ctx.fillText(s, x, y);
  resetSpacing(ctx);
  return width;
}

/** Latin names are set in spaced capitals (luxury look); Thai names as typed. */
const display = (s: string) => (isLatin(s) ? s.toUpperCase() : s);
const spacingFor = (s: string, latin: number) => (isLatin(s) ? latin : 0);

function drawLogo(ctx: CanvasRenderingContext2D, logo: Img, x: number, y: number, w: number, h: number) {
  ctx.save();
  roundRect(ctx, x, y, w, h, Math.min(w, h) * 0.12);
  ctx.clip();
  ctx.drawImage(logo, x, y, w, h);
  ctx.restore();
}

/** Name (+ tagline) as a centred block around `cx`, between thin rules when there is a tagline. */
function drawNameBlock(ctx: CanvasRenderingContext2D, t: MenuTheme, name: string, tagline: string, left: number, right: number, align: 'center' | 'left') {
  const { y, height } = BRANDED_LAYOUT.header;
  const maxW = right - left;
  const x = align === 'center' ? (left + right) / 2 : left;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  if (name) {
    ctx.fillStyle = t.brand;
    fitText(ctx, display(name), x, y + (tagline ? 90 : height / 2 + 4), maxW, 104, 700, spacingFor(name, 0.08));
  }
  if (tagline) {
    ctx.fillStyle = t.muted;
    const ty = y + (name ? 170 : height / 2 + 4);
    const tw = fitText(ctx, display(tagline), x, ty, Math.min(maxW, 1600), 52, 500, spacingFor(tagline, 0.16));
    if (align === 'center') {
      const half = tw / 2 + 44;
      ctx.strokeStyle = t.line;
      ctx.globalAlpha = 0.7;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x - half - 150, ty); ctx.lineTo(x - half, ty);
      ctx.moveTo(x + half, ty); ctx.lineTo(x + half + 150, ty);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }
}

function drawBrand(ctx: CanvasRenderingContext2D, a: MenuArt) {
  const { y, height } = BRANDED_LAYOUT.header;
  const t = a.theme;
  const name = (a.name ?? '').trim();
  const tagline = (a.tagline ?? '').trim();
  const logo = hasSize(a.logo) ? a.logo : null;

  if (logo && (logo.width / logo.height >= WIDE_LOGO || (!name && !tagline))) {
    // Full logo (or nothing else to show): the logo alone, centred.
    const boxW = MENU_WIDTH - 2 * PAD, boxH = height - 2 * 24;
    const s = Math.min(boxW / logo.width, boxH / logo.height);
    const w = logo.width * s, h = logo.height * s;
    drawLogo(ctx, logo, (MENU_WIDTH - w) / 2, y + (height - h) / 2, w, h);
    return;
  }
  if (logo) {
    // Square-ish logo beside the name: measure the name, then centre logo + name together.
    const lh = 172, lw = Math.min(lh * (logo.width / logo.height), 380);
    const gap = 44;
    const textMax = MENU_WIDTH - 2 * PAD - lw - gap;
    const nameW = name ? fitSize(ctx, display(name), textMax, 104, 700, spacingFor(name, 0.08)).width : 0;
    const tagW = tagline ? fitSize(ctx, display(tagline), Math.min(textMax, 1600), 52, 500, spacingFor(tagline, 0.16)).width : 0;
    resetSpacing(ctx);
    const blockW = lw + gap + Math.max(nameW, tagW);
    const x0 = (MENU_WIDTH - blockW) / 2;
    drawLogo(ctx, logo, x0, y + (height - lh) / 2, lw, lh);
    drawNameBlock(ctx, t, name, tagline, x0 + lw + gap, x0 + lw + gap + textMax, 'left');
    return;
  }
  if (name || tagline) drawNameBlock(ctx, t, name, tagline, PAD + 100, MENU_WIDTH - PAD - 100, 'center');
  // Nothing given: the bar stays blank.
}

function drawBanner(ctx: CanvasRenderingContext2D, a: MenuArt) {
  const t = a.theme;
  const x = PAD, y = BRANDED_LAYOUT.banner.y, w = MENU_WIDTH - 2 * PAD, h = BRANDED_LAYOUT.banner.height - 30;
  ctx.save();
  roundRect(ctx, x, y, w, h, 40);
  ctx.clip();
  if (!hasSize(a.photo)) {
    // No photo: a blank panel, waiting for the salon's picture.
    ctx.fillStyle = t.blank;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
    return;
  }
  const photo = a.photo;
  const s = Math.max(w / photo.width, h / photo.height);
  const dw = photo.width * s, dh = photo.height * s;
  const focus = Math.min(100, Math.max(0, a.photoFocus ?? 50)) / 100;
  ctx.drawImage(photo, x + (w - dw) / 2, y + (h - dh) * focus, dw, dh);
  // Soft shade at the bottom so the "tap to book" pill reads on any photo.
  const shade = ctx.createLinearGradient(0, y + h - 240, 0, y + h);
  shade.addColorStop(0, 'rgba(0,0,0,0)');
  shade.addColorStop(1, 'rgba(0,0,0,0.32)');
  ctx.fillStyle = shade;
  ctx.fillRect(x, y + h - 240, w, 240);
  ctx.restore();

  ctx.font = `600 56px ${FONT}`;
  const label = 'แตะเพื่อจองคิว  ›';
  const pw = ctx.measureText(label).width + 100, ph = 104;
  const px = x + w - pw - 36, py = y + h - ph - 36;
  ctx.fillStyle = 'rgba(17,17,17,0.7)';
  roundRect(ctx, px, py, pw, ph, ph / 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, px + pw / 2, py + ph / 2 + 2);
}

function drawIcon(ctx: CanvasRenderingContext2D, name: IconName, cx: number, cy: number, size: number, color: string) {
  if (typeof Path2D === 'undefined') return;
  const s = size / 24;
  ctx.save();
  ctx.translate(cx - size / 2, cy - size / 2);
  ctx.scale(s, s);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(new Path2D(ICONS[name]));
  ctx.restore();
}

function drawCard(ctx: CanvasRenderingContext2D, t: MenuTheme, x: number, y: number, w: number, h: number) {
  roundRect(ctx, x, y, w, h, 32);
  ctx.fillStyle = t.card;
  ctx.fill();
  ctx.strokeStyle = t.line;
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function drawButtons(ctx: CanvasRenderingContext2D, t: MenuTheme) {
  const { y, height, cols } = BRANDED_LAYOUT.buttons;
  const top = y + 30;
  const w = (MENU_WIDTH - 2 * PAD - (cols - 1) * GAP) / cols;
  const h = height - 30 - PAD;
  BUTTONS.forEach((b, i) => {
    const bx = PAD + i * (w + GAP);
    drawCard(ctx, t, bx, top, w, h);
    drawIcon(ctx, b.icon, bx + w / 2, top + h * 0.27, 96, t.line);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = t.text;
    fitText(ctx, b.label, bx + w / 2, top + h * 0.6, w - 60, 76, 700);
    ctx.fillStyle = t.muted;
    fitText(ctx, b.sub, bx + w / 2, top + h * 0.83, w - 60, 46, 500);
  });
}

/** Draws the whole menu at full size. `ctx` must belong to a 2500×1336 canvas. */
export function drawCustomerMenu(ctx: CanvasRenderingContext2D, a: MenuArt) {
  ctx.fillStyle = a.theme.bg;
  ctx.fillRect(0, 0, MENU_WIDTH, BRANDED_HEIGHT);
  drawBrand(ctx, a);
  drawBanner(ctx, a);
  drawButtons(ctx, a.theme);
}

/** Staff menu (2500×843): 4 buttons in one row, same style. Used for the default staff picture. */
export const STAFF_BUTTONS: { icon: IconName; label: string }[] = [
  { icon: 'list', label: 'คิววันนี้' },
  { icon: 'calendar', label: 'ปฏิทิน' },
  { icon: 'plus', label: 'จองให้ลูกค้า' },
  { icon: 'sliders', label: 'จัดการร้าน' },
];

export function drawStaffMenu(ctx: CanvasRenderingContext2D, t: MenuTheme) {
  const H = 843;
  ctx.fillStyle = t.bg;
  ctx.fillRect(0, 0, MENU_WIDTH, H);
  const w = (MENU_WIDTH - 2 * PAD - 3 * GAP) / 4, h = H - 2 * PAD;
  STAFF_BUTTONS.forEach((b, i) => {
    const bx = PAD + i * (w + GAP);
    drawCard(ctx, t, bx, PAD, w, h);
    drawIcon(ctx, b.icon, bx + w / 2, PAD + h * 0.36, 170, t.line);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = t.text;
    fitText(ctx, b.label, bx + w / 2, PAD + h * 0.72, w - 70, 92, 700);
  });
}
